import {
  Library,
  CheckCircle2,
  BookMarked,
  Compass,
  Coffee,
  Sparkles,
} from "lucide-react";

export const FEATURE_PILLARS = [
  {
    title: "Organize without overwhelm",
    desc: "Your shelf is a living catalog — not a spreadsheet, not a performance scoreboard.",
  },
  {
    title: "Discover through people",
    desc: "Recommendations travel friend-to-friend inside circles and curated shelves.",
  },
  {
    title: "Read in a calmer interface",
    desc: "Warm typography, gentle motion, and zero clutter competing with the story.",
  },
];

export const FEATURE_SECTIONS = [
  {
    id: "manage-shelf",
    icon: Library,
    title: "Manage my shelf",
    tagline: "A home for every book you own, want, or have finished.",
    paragraphs: [
      "PagePeers starts with your shelf — a personal library that grows with you. Add titles in seconds, drag them into the order that makes sense, and keep separate lists for owned books, wishlists, and gifts you have not opened yet.",
      "Unlike rigid catalog apps, there is no pressure to log every detail on day one. Start with a title and author; add notes, editions, and tags when you feel like it. Your shelf should feel like a desk you actually use, not a database you maintain.",
    ],
    bullets: [
      "Add books manually or from shared community lists",
      "Custom shelves: reading now, up next, favorites, lent out",
      "Soft filters by genre, mood, or format (paperback, ebook, audio)",
      "Private by default — you choose what circles can see",
    ],
    highlight: "Most members organize their first shelf in under five minutes.",
  },
  {
    id: "read-status",
    icon: CheckCircle2,
    title: "Mark read / unread",
    tagline: "Progress tracking that respects your pace.",
    paragraphs: [
      "A single tap moves a book between unread, reading, and finished — no complicated percentages unless you want them. PagePeers treats status as a gentle signal, not a grade.",
      "Finished books stay on your shelf as a quiet archive of what you have lived through. Unread books wait without guilt. Currently reading sits at the top so you always know where to return.",
    ],
    bullets: [
      "One-click status updates from any device",
      "Optional finish dates for your personal reading timeline",
      "Re-read a favorite without losing your history",
      "Circle members see only what you choose to share",
    ],
    highlight: "Readers who track status finish 2× more books they start.",
  },
  {
    id: "collection",
    icon: BookMarked,
    title: "View my collection",
    tagline: "See your whole reading life in one calm overview.",
    paragraphs: [
      "Your collection view is the bird’s-eye map: everything you have touched, sorted the way you think — by color on the spine, by season you read it, or by who recommended it.",
      "Jump back into a half-finished novel, rediscover a book you loved three winters ago, or spot gaps in a series before you visit the shop. The layout stays readable whether you have twenty books or two thousand.",
    ],
    bullets: [
      "Grid, list, and compact shelf layouts",
      "Search across title, author, and your own notes",
      "Sort by date added, date finished, or custom order",
      "Export-friendly for your own backups",
    ],
    highlight: "Built for collectors who care more about memory than metrics.",
  },
  {
    id: "discover",
    icon: Compass,
    title: "Discover other shelves",
    tagline: "Find your next read through real readers, not algorithms.",
    paragraphs: [
      "Browse public shelves from the PagePeers community — staff picks, themed collections, and member-curated lists built around moods like rainy afternoons, debut authors, or slow Sundays.",
      "Every shelf comes with context: who built it, why these books belong together, and what readers are saying in the comments. Discovery feels like a bookstore recommendation, not an autoplay feed.",
    ],
    bullets: [
      "Follow shelves and get updates when new titles appear",
      "Save any book to your wishlist in one tap",
      "Explore by tag: cozy, challenging, translated, book club",
      "See what your circles are reading this week",
    ],
    highlight: "850+ public shelves and growing every week.",
  },
  {
    id: "calm-flow",
    icon: Coffee,
    title: "Calm reading flow",
    tagline: "Software that steps back so the book can step forward.",
    paragraphs: [
      "PagePeers is designed like a reading room: warm backgrounds, serif headlines, unhurried animations, and no red notification dots demanding attention. Login is quick; the interface stays out of your way.",
      "Dark mode for late chapters, light mode for morning coffee, and typography tuned for long reading sessions on any screen size. We measure success by how rarely you think about the app while using it.",
    ],
    bullets: [
      "Distraction-free layouts with generous spacing",
      "Light and dark themes that match time of day",
      "Accessible contrast and readable type scale",
      "No autoplay, no infinite scroll, no engagement traps",
    ],
    highlight: "Average session length: long enough to finish a chapter.",
  },
  {
    id: "habits",
    icon: Sparkles,
    title: "Reading habits",
    tagline: "Gentle nudges that help consistency feel kind.",
    paragraphs: [
      "Optional reminders arrive when you want them — Sunday shelf reviews, circle highlights, or a soft ping when a book you saved goes on sale. You control frequency; PagePeers never spams.",
      "Monthly reading reflections show patterns without judgment: genres you gravitated toward, books you started but set aside, circles that inspired you most. Use them to plan the next season, not to optimize a streak.",
    ],
    bullets: [
      "Weekly digest of circle activity (opt in)",
      "Personal reading streaks — private or shared",
      "Seasonal shelf prompts from editors",
      "Mute anything in one click",
    ],
    highlight: "Members who enable gentle nudges read 40% more pages per month.",
  },
];

export const FEATURE_FAQ = [
  {
    q: "Is PagePeers free to use?",
    a: "You can build your shelf, browse public collections, and join circles on the free tier. Premium adds advanced organization and hosting tools for power readers.",
  },
  {
    q: "Can I keep my shelf private?",
    a: "Yes. Everything is private until you explicitly share a shelf or join a circle that asks for visibility.",
  },
  {
    q: "Does PagePeers replace Goodreads?",
    a: "For many readers, yes — especially if you want calmer design and smaller discussion groups. You can also use PagePeers alongside other tools.",
  },
  {
    q: "How do reader circles work?",
    a: "Small groups (often 8–20 people) pick a book or theme, share highlights weekly, and discuss in spoiler-safe threads. Browse open circles or start your own.",
  },
  {
    q: "Can I import my existing library?",
    a: "Import from CSV or add books in bulk from any public shelf you follow. We are expanding direct import options over time.",
  },
];
